/**
 * Burst selection (Phase 3 — L4).
 *
 * Pure client utility: given several captured frames (as data URLs), pick the
 * sharpest one using the same Laplacian-variance focus measure the
 * CaptureCoach already uses, then return both the winner and the per-frame
 * scores so the UI can show why a shot was chosen.
 *
 * Runs on canvas 2D — no WASM, no native deps, no SSR.
 */

const MAX_SAMPLE = 320;
const ROI_FRAC = 0.7;

export type BurstScore = {
  index: number;
  dataUrl: string;
  sharpness: number;     // Laplacian variance, higher = sharper
  brightness: number;    // 0..255 mean luma (ROI)
};

export type BurstResult = {
  bestIndex: number;
  scores: BurstScore[];
};

export async function selectBestFrame(dataUrls: string[]): Promise<BurstResult> {
  if (dataUrls.length === 0) throw new Error("no_frames");
  const scores: BurstScore[] = [];
  for (let i = 0; i < dataUrls.length; i++) {
    const url = dataUrls[i];
    try {
      const m = await measureOne(url);
      scores.push({ index: i, dataUrl: url, ...m });
    } catch {
      scores.push({ index: i, dataUrl: url, sharpness: 0, brightness: 0 });
    }
  }
  // Penalise extreme brightness (too dark / blown out) when picking the best.
  let bestIndex = 0;
  let bestRank = -Infinity;
  for (const s of scores) {
    const bPenalty = s.brightness < 40 || s.brightness > 225 ? 0.5 : 1;
    const rank = s.sharpness * bPenalty;
    if (rank > bestRank) { bestRank = rank; bestIndex = s.index; }
  }
  return { bestIndex, scores };
}

function measureOne(dataUrl: string): Promise<{ sharpness: number; brightness: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, MAX_SAMPLE / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * ratio));
      const h = Math.max(1, Math.round(img.naturalHeight * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return reject(new Error("no_ctx"));
      ctx.drawImage(img, 0, 0, w, h);
      const roi = Math.floor(Math.min(w, h) * ROI_FRAC);
      const rx = Math.floor((w - roi) / 2);
      const ry = Math.floor((h - roi) / 2);
      const { data } = ctx.getImageData(rx, ry, roi, roi);
      const luma = new Float32Array(roi * roi);
      let sum = 0;
      for (let i = 0, j = 0; i < data.length; i += 4, j++) {
        const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        luma[j] = y; sum += y;
      }
      const brightness = sum / luma.length;
      // Laplacian variance
      let s = 0, sq = 0, n = 0;
      for (let y = 1; y < roi - 1; y++) {
        for (let x = 1; x < roi - 1; x++) {
          const i = y * roi + x;
          const v = luma[i - roi] + luma[i + roi] + luma[i - 1] + luma[i + 1] - 4 * luma[i];
          s += v; sq += v * v; n++;
        }
      }
      const mean = s / n;
      const sharpness = sq / n - mean * mean;
      canvas.width = canvas.height = 0;
      resolve({ sharpness, brightness });
    };
    img.onerror = () => reject(new Error("img_load"));
    img.src = dataUrl;
  });
}